import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useForm, type FieldErrors } from 'react-hook-form'
import { Link, useSearchParams } from 'react-router-dom'
import { useCriarGmud } from '../../../api/queries/gmuds'
import { temPermissao, useSessao } from '../../../auth/contexto'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro, EstadoVazio } from '../../../components/Estados'
import { TERMOS } from '../../../lib/glossario'
import type { DetalheGmud } from '../../../types/gmud'
import { ConclusaoDoCadastro } from './ConclusaoDoCadastro'
import { EtapaClassificacao } from './EtapaClassificacao'
import { EtapaConferencia } from './EtapaConferencia'
import { EtapaPlano } from './EtapaPlano'
import { LinkDeAcao } from './LinkDeAcao'
import { PassosDoAssistente } from './PassosDoAssistente'
import { paraRequisicao, type FormularioNovaGmud } from './esquema'
import {
  ETAPAS,
  PARAMETRO_DA_ETAPA,
  etapaAnterior,
  lerEtapa,
  primeiraEtapaComErro,
  proximaEtapa,
  type Etapa,
} from './etapas'

/**
 * Abertura de uma solicitação de mudança, em três etapas.
 *
 * O formulário inteiro numa tela só é o que faz o servidor leigo em TI desistir no meio ou
 * preencher mal — são quatorze campos, oito deles de texto longo. Aqui a máquina de etapas é
 * explícita e cada uma tem o seu esquema zod (`etapas.ts`): o resolver do formulário é o da etapa
 * corrente, então "Continuar" não passa por cima de um campo inválido, e o erro aparece no campo
 * onde ele está.
 *
 * A etapa vai para a URL (`?etapa=2`). Recarregar a página não muda de tela — o preenchimento,
 * esse sim, se perde: o rascunho só passa a existir no servidor depois de salvo, e guardá-lo no
 * `localStorage` criaria uma segunda verdade sobre uma solicitação de mudança. Quando o formulário
 * volta vazio, a conferência mostra os campos em branco e o "Salvar" devolve a pessoa à etapa 1.
 *
 * Esconder o link no menu não basta: este endereço é colável, e um "abre uma GMUD pra mim" com o
 * link no corpo do e-mail entrega o formulário inteiro a quem só tem leitura. Quem decide continua
 * sendo o servidor — `GMUD_CRIAR` vem de `GET /me`, derivado da mesma matriz que
 * `GmudCriacaoService` consulta; a tela só deixa de cobrar meia hora de preenchimento para
 * responder no fim que o perfil nunca permitiu.
 *
 * @returns a tela de abertura
 */
export function PaginaNovaGmud() {
  const [parametros, definirParametros] = useSearchParams()
  const criar = useCriarGmud()
  const { usuario } = useSessao()
  const [criada, definirCriada] = useState<DetalheGmud | null>(null)

  const etapa = lerEtapa(parametros)
  const proxima = proximaEtapa(etapa)
  const anterior = etapaAnterior(etapa)

  const {
    register,
    handleSubmit,
    watch,
    control,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormularioNovaGmud>({
    resolver: zodResolver(etapa.esquema),
    defaultValues: { causaEmergencial: '', responsavel: '' },
  })

  const irPara = useCallback(
    (destino: Etapa) => {
      const proximos = new URLSearchParams(parametros)
      proximos.set(PARAMETRO_DA_ETAPA, String(destino.numero))
      // Empilha no histórico: "voltar" do navegador desfaz uma etapa em vez de abandonar o
      // formulário inteiro, que é o que o usuário espera de um assistente.
      definirParametros(proximos)
    },
    [parametros, definirParametros],
  )

  const aoReprovar = useCallback(
    (problemas: FieldErrors<FormularioNovaGmud>) => {
      const alvo = primeiraEtapaComErro(problemas)
      if (alvo && alvo.numero !== etapa.numero) {
        irPara(alvo)
      }
    },
    [etapa.numero, irPara],
  )

  const avancar = handleSubmit(() => {
    if (proxima) {
      irPara(proxima)
    }
  }, aoReprovar)

  // `mutateAsync` rejeita quando o servidor recusa, e o `onSubmit` do react-hook-form repassa a
  // rejeição adiante — o React não a consome, e ela vira `unhandled rejection` no console e em
  // qualquer coletor de erro ligado à página. O erro já está na tela por `criar.isError`; aqui ele
  // só precisa parar de escapar.
  const salvar = handleSubmit(async (valores) => {
    try {
      definirCriada(await criar.mutateAsync(paraRequisicao(valores, usuario?.id ?? null)))
    } catch {
      // Tratado por <AvisoDeErro erro={criar.error} />.
    }
  }, aoReprovar)

  if (!temPermissao(usuario, 'GMUD_CRIAR')) {
    return (
      <EstadoVazio
        titulo="Você não tem permissão para abrir solicitações"
        ajuda="Seu perfil permite consultar as solicitações, mas não abrir novas. Se você precisa abrir uma mudança, fale com a equipe da SSTD."
        acao={
          <Link to="/gmuds" className="text-sm text-niteroi-700 underline underline-offset-2">
            Ver as solicitações
          </Link>
        }
      />
    )
  }

  if (criada) {
    return <ConclusaoDoCadastro gmud={criada} />
  }

  return (
    // Formulário em largura de leitura: com a área de conteúdo mais larga, os campos em duas colunas
    // esticavam a 600px cada e o olho perdia o rótulo da linha.
    <form onSubmit={proxima ? avancar : salvar} className="max-w-5xl space-y-6" noValidate>
      <header>
        <h1 className="titulo-da-pagina">Abrir {TERMOS.gmud.rotulo.toLowerCase()}</h1>
        <p className="mt-1 max-w-prose text-sm text-slate-600">{TERMOS.gmud.ajuda}</p>
      </header>

      <PassosDoAssistente etapaAtual={etapa} aoIr={irPara} />

      {criar.isError && <AvisoDeErro erro={criar.error} titulo="A solicitação não foi criada" />}

      <PainelDaEtapa etapa={etapa}>
        {etapa.id === 'classificacao' && (
          <EtapaClassificacao
            register={register}
            errors={errors}
            emergencial={watch('prioridade') === 'EMERGENCIAL'}
            control={control}
          />
        )}
        {etapa.id === 'plano' && <EtapaPlano register={register} errors={errors} control={control} />}
        {etapa.id === 'conferencia' && <EtapaConferencia valores={getValues()} aoCorrigir={irPara} />}
      </PainelDaEtapa>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {anterior && <Botao onClick={() => irPara(anterior)}>Voltar</Botao>}
          <LinkDeAcao to="/gmuds">Cancelar</LinkDeAcao>
        </div>
        <Botao type="submit" aparencia="primario" disabled={isSubmitting}>
          {proxima ? 'Continuar' : isSubmitting ? 'Salvando…' : 'Salvar rascunho'}
        </Botao>
      </div>
    </form>
  )
}

/**
 * O conteúdo da etapa, com o foco indo para ela na troca.
 *
 * Sem isto, quem navega por teclado clica em "Continuar", a tela troca inteira e o foco continua
 * no botão — que agora pertence a outra etapa. Para quem usa leitor de tela nada é anunciado: a
 * página não recarregou, e a mudança aconteceu fora do ponto de leitura. O título é `sr-only`
 * porque a barra de etapas já diz o mesmo a quem enxerga.
 *
 * @param props a etapa corrente e o conteúdo dela
 * @returns a região da etapa
 */
function PainelDaEtapa({ etapa, children }: { etapa: Etapa; children: ReactNode }) {
  const cabecalho = useRef<HTMLHeadingElement>(null)
  const anterior = useRef(etapa.id)

  useEffect(() => {
    if (anterior.current !== etapa.id) {
      anterior.current = etapa.id
      cabecalho.current?.focus()
    }
  }, [etapa.id])

  return (
    <section>
      <h2 ref={cabecalho} tabIndex={-1} className="sr-only focus:outline-none">
        Etapa {etapa.numero} de {ETAPAS.length}: {etapa.rotulo}. {etapa.resumo}
      </h2>
      {children}
    </section>
  )
}
